/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 12 2024
 **/

import { useEffect } from "react";
import { isEmpty } from "lodash-es";
import { useAtomValue } from "jotai";
import { useEventHandlers } from "@rfice/eventhandler";
//*
import { SoundMeter } from "@/frameworks/core/engine";
/*/
import { SoundMeter } from "@rfice/xms";
//*/

import { JotaiStore } from "@/frameworks/libraries/commons";
import { useBeforeUnload } from "@/frameworks/libraries/hooks";
import { useStompClient } from "@/frameworks/libraries/network";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Properties } from "@/frameworks/libraries/properties";
import { useToast } from "@/frameworks/designStuff/components";

import { EventID, SidebarID } from "@/frameworks/core/commons";
import { usePhoneCall, PhoneCallAtoms, useSidebarActions } from "@/frameworks/core/hooks";
import { PhoneCallPopupPortals } from "@/frameworks/core/components";
import { PhoneBooths } from "@/frameworks/core/components";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useNetworkCommand } from "@/frameworks/core/network";
import { useCall } from "@/frameworks/core/API";

const LOGTAG = "#PhoneCallProvider#";

const CALL_NOT_CONNECTED_STATE = ["ENDED", "REJECTED", "HANGUP", "HANGUP_NO_RESPONSE", "ENDED_BY_DEVICE_DISCONNECTED"];

export default function PhoneCallProvider() {
	const dispatch = usePersistActions();
	const phoneCall = usePhoneCall();
	const callList = useAtomValue(PhoneCallAtoms.CallList, { store: JotaiStore });
	const setPhoneDetail = phoneCall.state.phone_detail[1];
	const toast = useToast();
	const stompCommand = useNetworkCommand();
	const callAPI = useCall();

	useStompClient(
		{
			async onMessageReceived({ type, payload }) {
				if (type === "groupCallStatusChanged") {
					const { callId, callUsers, callerId, status } = payload as PushServices.Call.StatusChanged.Data;
					// 같은 계정 다른 장치에서 통화 시작했는지 감지
					if (
						Configure.userID === callerId &&
						callUsers.filter((user) => user.userId === Configure.userID)?.[0]?.deviceKey !==
							Properties.browserTabID
					) {
						return;
					}
					// 통화 참여 상태가 아니고, cleanUp으로 callList도 비어있을 경우 phoneCall update 하지 않음
					if (
						CALL_NOT_CONNECTED_STATE.includes(
							callUsers?.filter((user) => user.userId === Configure.userID)[0]?.status
						) &&
						callList &&
						Object.values(callList).length === 0
					) {
						return;
					}
					const currentCallStatus = (
						await callAPI.groupCall.detail.execute({
							callId: callId
						})
					)?.data?.status;
					if (status === "READY" && CALL_NOT_CONNECTED_STATE.includes(currentCallStatus ?? "")) {
						return;
					}
					if (callId) {
						phoneCall.add_or_update_call(payload);
					}
					if (isEmpty(callList)) {
						return;
					}
					const prevCallUsers = Object.values(callList[callId]?.callUsers ?? {});
					if (!prevCallUsers) return;
					if (callList[callId]?.callerId !== Configure.userID) return;
					const prevUserMap = new Map(prevCallUsers.map((user) => [user.userId, user.status]));
					if (callUsers.length === 2) {
						// 내가 통화 끊었을 때: 나 END, 상대 HANGUP_NO_RESPONSE
						// 상대가 끊었을때: 나 END, 상대 REJECTED
						// 통화 받지 않아서 끊겼을 때: 둘 다 HANGUP_NO_RESPONSE
						const myStatus = callUsers?.find((user) => user.userId === Configure.userID)?.status;
						const otherStatus = callUsers?.find((user) => user.userId !== Configure.userID)?.status;
						if (myStatus === "ENDED" && otherStatus === "HANGUP_NO_RESPONSE") {
							// 내가 끊은 경우 토스트 노출 x
							return;
						} else if (myStatus === "ENDED" && otherStatus === "REJECTED") {
							toast.show({
								type: "error",
								message: _T("전화가 연결되지 않았습니다.")
							});
						}
					} else {
						// 내가 끊은 경우 토스트 노출 x
						if (callUsers?.find((user) => user.userId === Configure.userID)?.status === "ENDED") return;
						for (const currentUser of callUsers) {
							const prevStatus = prevUserMap.get(currentUser.userId);
							if (prevStatus === "REQUESTED" && currentUser.status === "HANGUP_NO_RESPONSE") {
								toast.show({
									type: "error",
									message: _T("전화가 연결되지 않았습니다.")
								});
							} else if (prevStatus === "REQUESTED" && currentUser.status === "REJECTED") {
								toast.show({
									type: "error",
									message: _T("통화가 거절되었습니다.")
								});
							}
						}
					}
				}
			}
		},
		[callList]
	);

	useEventHandlers(
		{
			async [EventID.OPEN_PHONE_DETAIL](
				callerID: string,
				calleeUserList: string[] | WebServices.User.UserInfo[],
				tabID: string,
				callID: string = "",
				isFromRecent?: boolean
			) {
				const callees: WebServices.User.UserInfo[] = [];
				if (calleeUserList) {
					for await (const callee of calleeUserList) {
						if (typeof callee === "string") {
							const user = dispatch.users.get_user(callee);
							user && callees.push(user as WebServices.User.UserInfo);
						} else {
							callees.push(callee);
						}
					}
				}
				setPhoneDetail({ callerID, callees, tabID, callID, isFromRecent });
			},
			onLocalStreamChanged(callID: string, userID: string, media: MediaStream, soundMeter: SoundMeter) {
				console.debug(LOGTAG, "local stream changed with", callID, userID, media);
				// phoneCall.stream.local_stream = media;
				phoneCall.stream.sound_meter = soundMeter;
			},
			onRemoteStreamChanged(callID: string, userID: string, media: MediaStream) {
				console.debug(LOGTAG, "remote stream changed with", callID, userID, media);
				phoneCall.stream.set_remote_stream(userID, media);
			},
			onScreenStreamChanged(callID: string, userID: string, media: MediaStream) {
				console.debug(LOGTAG, "remote screen stream changed with", callID, userID, media);

				const userIDWithScreen = userID.replace("screen_", "");
				const isUserScreenSharing = phoneCall.get_user_media(callID, userIDWithScreen)?.screenShare;

				if (media === null && userID === Configure.userID) {
					phoneCall.screen_update(callID, userID, "stop");
					phoneCall.stream.remote_screen_stream = media;
					return;
				}
				if (media) {
					phoneCall.stream.remote_screen_stream = media;
					stompCommand.startScreenShare(callID, userID);
					return;
				}
				if (isUserScreenSharing && media === null) {
					// 유저가 화면 공유하고있는데 미디어는 없는 경우 (공유 중지 눌러서 온 케이스)
					phoneCall.screen_update(callID, userIDWithScreen, "stop");
					phoneCall.stream.remote_screen_stream = media;
					return;
				}
			},
			onRemovePeerViewChanged(callID: string, leaveUserID: string) {
				//남아있는 인원이 removePeerView한 유저와 나만 있을 때 (서버에서 통화종료는 오지 않았지만 미디어 서버에서 나갔다는 메시지가 왔을때)
				if (!isEmpty(callList?.[callID])) {
					const remainingUser = Object.values(callList?.[callID]?.callUsers)?.filter(
						(user) => user?.status === "ACCEPTED" && user.userId !== Configure.userID
					);
					if (remainingUser?.length === 1 && remainingUser?.[0]?.userId === leaveUserID) {
						phoneCall.call_end(callID);
					}
				}
			},
			async onPhoneCall(callID: string, callerID: string) {
				if (callID.length <= 0) {
					callID = await phoneCall.start_dial({
						type: "normal",
						callerIDs: [callerID]
					});
				}
				phoneCall.popup.show({
					callID,
					callerID: callerID,
					uiType: "floating"
				});
			}
		},
		[callList]
	);

	useBeforeUnload(
		async (event: Event) => {
			Object.entries(callList).length !== 0 && (await phoneCall.allCallEnding());
		},
		[callList]
	);

	const sidebarActions = useSidebarActions();

	useEffect(() => {
		if (phoneCall.state.active.activeCallID) {
			//active한 call이 있고, 현재 popup이 떠있지 않고, Call 패널이 안열려있으면 (active한 통화가 있지만 아무 ui 출력 없는 경우)
			if (
				phoneCall.popup.info(phoneCall.state.active.activeCallID)?.uiType === undefined &&
				!sidebarActions.isOpen(SidebarID.PhoneDetail)
			) {
				phoneCall.popup.show({
					callID: phoneCall.state.active.activeCallID,
					callerID: Configure.userID,
					uiType: "floating"
				});
			}
		}
	}, [phoneCall.state.active.activeCallID]);

	return (
		<>
			<PhoneCallPopupPortals />
			<PhoneBooths />
		</>
	);
}
